<template>
  <header class="page-header">
    <h1>Companies</h1>

    <div class="page-button-group">
      <GuLink v-if="canManageCompanies" variant="link" :to="{ name: 'companies:history' }">
        History
      </GuLink>
    </div>
  </header>

  <div class="flex-fill-parent mx-5">
    <BlockError v-if="error" can-retry @retry="refetch">
      <h2 class="h5">Error loading companies</h2>
      {{ error?.message }}
    </BlockError>

    <GuDatagridClient
      v-else
      :config="config"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<script setup>
import { ref, watchEffect } from 'vue'

import { GuDatagridClient } from '@gu-datagrid-new'
import { GuLink } from '@shared/gu-components/link'
import BlockError from '@shared/components/BlockError.vue'

import { useTriggerRule } from '@companies/api/companies.api'

import { buildPgeCompaniesGridConfig } from '@customers/pge/views/companies/companies.dashboard.datagrid.config'

// Path B: Java controller invokes the Python `getAllCompanies` rule via the
// legacy /Queue/PageEvent endpoint and returns the rule's JSON output verbatim.
// The rule emits `{companies: [{ID, Company, CSLB, Attributes, Status, Actions, ...}]}`
// already shaped for the PG&E grid - no normalization required for known fields.
//
// The grid mounts immediately (not behind v-if) because AG-Grid Vue's intermediate
// wrapper otherwise measures at content-size and rows render outside the viewport.
const privileges = window.siteVars?.user?.privileges || []
const canManageCompanies =
  privileges.includes('editCompanies') ||
  privileges.includes('editAllCompanies')

const userName = window.siteVars?.user?.name || ''

const { data, isPending: isLoading, error, refetch } = useTriggerRule(
  'getAllCompanies',
  { userName },
)

const gridApi = ref(null)
const config = ref(buildPgeCompaniesGridConfig({
  rowData: [],
  context: { canManageCompanies, refetch },
}))

function onGridReady (event) {
  gridApi.value = event.api
  if (isLoading.value) {
    gridApi.value.setGridOption('loading', true)
  }
}

watchEffect(() => {
  if (!gridApi.value) return
  gridApi.value.setGridOption('loading', isLoading.value)
  if (!isLoading.value && Array.isArray(data.value?.companies)) {
    gridApi.value.setGridOption('rowData', normalizeCompanies(data.value.companies))
  }
})

// PG&E rules return field names with capitalized keys (CSLB, Attributes, Phone Type)
// alongside `id` and `Company`. Normalize so the grid config can rely on a stable
// shape regardless of which backend (rule vs Java endpoint) provided the row.
function normalizeCompanies (companies) {
  return companies.map((row) => {
    // Pending company requests come back with id=null and a companyRequestId
    // instead. Fall back to the request id so the grid still has a unique row id.
    const id = row.ID ?? row.id ?? (row.companyRequestId != null ? `req-${row.companyRequestId}` : null)
    return {
      ID: id,
      rowId: row.rowId ?? id,
      Company: row.Company ?? row.name ?? '',
      Email: row.Email ?? row.email ?? '',
      Address: row.Address ?? row.address ?? '',
      City: row.City ?? row.city ?? '',
      State: row.State ?? row.state ?? '',
      Zip: row.Zip ?? row.zip ?? '',
      Phone: row.Phone ?? row.phone ?? '',
      'Phone Type': row['Phone Type'] ?? row.phoneType ?? '',
      Status: row.Status ?? row.status ?? '',
      CSLB: row.CSLB ?? '',
      Attributes: Array.isArray(row.Attributes) ? row.Attributes : [],
      Actions: Array.isArray(row.Actions) ? row.Actions : [],
      Roles: row.Roles ?? '',
    }
  })
}
</script>
